import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import { RxCross1 } from "react-icons/rx";
import { useCallback, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment";
import { FaCheck, FaRegFileAlt, FaTrash } from "react-icons/fa";
import { useMutation } from "@tanstack/react-query";
import { IoIosCloudUpload } from "react-icons/io";

import ErrorPopup from "../../../../common/particles.jsx/ErrorPopup";
import {
  deleteImage,
  uploadImage,
  checkStatusByEmail,
} from "../../../../../services/authApi";
import useDebounce from "../../../../hooks/useDebounce";
import {
  INVENTORY_ROLE,
  INVOICE_ROLE,
  PATIENT_CARE_ROLE,
  RETUR_ROLE,
  WAREHOUSE_ROLE,
} from "../../../../constants/constant";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1200,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const INITIAL_STAFF = {
  title_id: 0,
  role: [],
  fullname: "",
  sipa_id: "",
  sipa_expired: "",
  phone: "",
  email: "",
  files: {
    ktp: "",
    izin: "",
    spesimen_signature: "",
    type_ktp: "",
    type_izin: "",
    type_signature: "",
  },
};

const ModalAddStaff = ({
  open,
  setOpen,
  formik,
  index,
  dataTitle,
  isFetchingTitle,
  dataRole,
  isFetchingRole,
}) => {
  const [tempStaff, setTempStaff] = useState(INITIAL_STAFF);
  const [errorMessage, setErrorMessage] = useState("");

  const debounceEmail = useDebounce(tempStaff.email, 500);

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteClose = () => {
    formik.setFieldValue(`staff`, [
      ...formik.values.staff.slice(0, index),
      ...formik.values.staff.slice(index + 1),
    ]);
    setOpen(false);
  };

  const handleCloseSave = () => {
    formik.setFieldValue(
      `staff`,
      index < 0
        ? [...formik.values.staff, tempStaff]
        : [
            ...formik.values.staff.slice(0, index),
            tempStaff,
            ...formik.values.staff.slice(index + 1),
          ]
    );
    setOpen(false);
  };

  const handleClickPreview = (url) => {
    window.open(url, "_blank");
  };

  const {
    mutate: mutateCheckStatusByEmail,
    data: dataStatusEmail,
    isPending: isPendingStatusEmail,
  } = useMutation({
    mutationFn: checkStatusByEmail,
  });

  const { mutate: mutateUploadKTP, isPending: isPendingUploadKTP } =
    useMutation({
      mutationFn: uploadImage,
      onSuccess: (data) => {
        setTempStaff((prevVal) => ({
          ...prevVal,
          files: { ...prevVal.files, ktp: data.url, type_ktp: data.data_type },
        }));
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateDeleteKTP, isPending: isPendingDeleteKTP } =
    useMutation({
      mutationFn: deleteImage,
      onSuccess: () => {
        setTempStaff((prevVal) => ({
          ...prevVal,
          files: { ...prevVal.files, ktp: "", type_ktp: "" },
        }));
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateUploadSIPA, isPending: isPendingUploadSIPA } =
    useMutation({
      mutationFn: uploadImage,
      onSuccess: (data) => {
        setTempStaff((prevVal) => ({
          ...prevVal,
          files: {
            ...prevVal.files,
            izin: data.url,
            type_izin: data.data_type,
          },
        }));
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateDeleteSIPA, isPending: isPendingDeleteSIPA } =
    useMutation({
      mutationFn: deleteImage,
      onSuccess: () => {
        setTempStaff((prevVal) => ({
          ...prevVal,
          files: {
            ...prevVal.files,
            izin: "",
            type_izin: "",
          },
        }));
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateUploadSign, isPending: isPendingUploadSign } =
    useMutation({
      mutationFn: uploadImage,
      onSuccess: (data) => {
        setTempStaff((prevVal) => ({
          ...prevVal,
          files: {
            ...prevVal.files,
            spesimen_signature: data.url,
            type_signature: data.data_type,
          },
        }));
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateUploadTtk, isPending: isPendingUploadTtk } =
    useMutation({
      mutationFn: uploadImage,
      onSuccess: (data) => {
        setTempStaff((prevVal) => ({
          ...prevVal,
          evidence_pharmacist_technician: {
            ...prevVal.evidence_pharmacist_technician,
            practice_permit: data.url,
            practice_permit_type_file: data.data_type,
          },
        }));
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateDeleteSign, isPending: isPendingDeleteSign } =
    useMutation({
      mutationFn: deleteImage,
      onSuccess: () => {
        setTempStaff((prevVal) => ({
          ...prevVal,
          files: {
            ...prevVal.files,
            spesimen_signature: "",
            type_signature: "",
          },
        }));
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateDeleteTtk, isPending: isPendingDeleteTtk } =
    useMutation({
      mutationFn: deleteImage,
      onSuccess: () => {
        setTempStaff((prevVal) => ({
          ...prevVal,
          evidence_pharmacist_technician: {
            practice_permit_number: "",
            practice_permit: "",
            practice_permit_type_file: "",
          },
        }));
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const SuccessfulUploadComponent = ({ title, value, type, fileType }) => {
    return (
      <div>
        <div className="w-full bg-blue-500 text-white py-3 px-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaRegFileAlt className="text-[16px]" />
            <p>{title}</p>
          </div>
          <FaTrash
            className="text-red-500 hover:text-gray-300 cursor-pointer text-[14px]"
            onClick={() => {
              if (type === "ktp") mutateDeleteKTP({ value, fileType });
              if (type === "sipa") mutateDeleteSIPA({ value, fileType });
              if (type === "sign") mutateDeleteSign({ value, fileType });
              if (type === "suratTtk") mutateDeleteTtk({ value, fileType });
            }}
          />
        </div>
        <div
          className="w-full border-dashed p-4 border-b-2 border-l-2 border-r-2 border-gray-300 rounded-b-lg cursor-pointer hover:text-gray-300 flex flex-col justify-center items-center"
          onClick={() => handleClickPreview(value)}
        >
          {(type === "ktp" && isPendingDeleteKTP) ||
          (type === "sipa" && isPendingDeleteSIPA) ||
          (type === "sign" && isPendingDeleteSign) ||
          (type === "suratTtk" && isPendingDeleteTtk) ? (
            <CircularProgress />
          ) : (
            <FaCheck className="text-[64px] text-blue-500" />
          )}
          <p>Berhasil Diupload!</p>
        </div>
      </div>
    );
  };

  const handleSelectRole = useCallback(
    (selectedId) => {
      const selectedValue = tempStaff.role.findIndex((id) => id === selectedId);

      let updatedList = cloneDeep(tempStaff.role);

      if (selectedValue !== -1) {
        updatedList.splice(selectedValue, 1);
      } else {
        updatedList.push(selectedId);
      }
      setTempStaff((prevVal) => ({ ...prevVal, role: updatedList }));
    },
    [tempStaff.role]
  );

  const handleSelectCategory = useCallback(
    (checked, type) => {
      let updatedList = cloneDeep(tempStaff.role);
      let roleArr =
        type === "invoice"
          ? INVOICE_ROLE
          : type === "inventory"
          ? INVENTORY_ROLE
          : type === "patientCare"
          ? PATIENT_CARE_ROLE
          : type === "warehouse"
          ? WAREHOUSE_ROLE
          : type === "retur"
          ? RETUR_ROLE
          : [];

      roleArr.map((elem) => {
        const selectedValue = updatedList.findIndex((id) => id === elem);
        if (selectedValue === -1 && checked) updatedList.push(elem);
        if (selectedValue > -1 && !checked) {
          updatedList.splice(selectedValue, 1);
        }
      });
      setTempStaff((prevVal) => ({ ...prevVal, role: updatedList }));
    },
    [tempStaff.role]
  );

  // Checking email
  useEffect(() => {
    mutateCheckStatusByEmail({ email: debounceEmail });
  }, [debounceEmail]);

  useEffect(() => {
    if (open) {
      setTempStaff(index < 0 ? INITIAL_STAFF : formik.values.staff[index]);
    }
  }, [open]);

  return (
    <div className="text-[12px]">
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            ...style,
            maxWidth: "85%",
            overflowY: "auto",
            overflowX: "wrap",
            maxHeight: "80vh",
            padding: 0,
          }}
          className="text-left w-full "
        >
          <div className="px-8 py-4 overflow-x-auto">
            <div className="flex items-center justify-between text-[14px] font-semibold mb-4">
              Tambah Staff
              <RxCross1
                className="text-[18px] cursor-pointer hover:text-gray-300"
                onClick={handleClose}
              />
            </div>

            {tempStaff ? (
              <>
                <div>
                  <FormControl fullWidth>
                    <FormLabel>Jabatan Staff</FormLabel>
                    <Select
                      value={tempStaff?.title_id}
                      onChange={(e) => {
                        var tempVal = {
                          ...tempStaff,
                          title_id: Number(e.target.value),
                        };
                        if (Number(e.target.value) === 12)
                          tempVal = {
                            ...tempVal,
                            evidence_pharmacist_technician: {
                              practice_permit_number: "",
                              practice_permit: "",
                              practice_permit_type_file: "",
                            },
                          };
                        else delete tempVal.evidence_pharmacist_technician;
                        setTempStaff(tempVal);
                      }}
                      sx={{ marginBottom: 2 }}
                    >
                      {dataTitle && !isFetchingTitle
                        ? dataTitle.map((item, index) => (
                            <MenuItem value={item.id} key={index}>
                              {item.name}
                            </MenuItem>
                          ))
                        : null}
                    </Select>
                  </FormControl>

                  <div>
                    <p className="text-[14px] font-semibold">
                      Pilih Role Staff
                    </p>
                    <hr className="mt-2 my-3" />
                    <div className="grid grid-cols-4 gap-x-4 w-full">
                      <div className="grid grid-cols-1 gap-x-4">
                        {dataRole && !isFetchingRole
                          ? dataRole
                              .filter(
                                (item) =>
                                  ![
                                    // ...EXCLUDE_ROLE,
                                    ...INVOICE_ROLE,
                                    ...INVENTORY_ROLE,
                                    ...PATIENT_CARE_ROLE,
                                    ...WAREHOUSE_ROLE,
                                    ...RETUR_ROLE,
                                  ].includes(item.id)
                              )
                              .map((item, index) => {
                                return (
                                  <FormGroup key={index}>
                                    <FormControlLabel
                                      control={<Checkbox />}
                                      label={item.name}
                                      onChange={() => {
                                        handleSelectRole(item.id);
                                      }}
                                      checked={tempStaff?.role.includes(
                                        item.id
                                      )}
                                      disabled={tempStaff?.title_id === 10}
                                    />
                                  </FormGroup>
                                );
                              })
                          : null}
                      </div>
                      <div>
                        <FormGroup sx={{ marginBottom: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={INVOICE_ROLE.every((elem) =>
                                  tempStaff?.role.includes(elem)
                                )}
                                onChange={(_, checked) => {
                                  handleSelectCategory(checked, "invoice");
                                }}
                                disabled={tempStaff?.title_id === 10}
                              />
                            }
                            label="Invoice"
                          />
                          <div className="grid grid-cols-1 gap-x-4">
                            {dataRole && !isFetchingRole
                              ? dataRole
                                  .filter((item) =>
                                    INVOICE_ROLE.includes(item.id)
                                  )
                                  .map((item, index) => {
                                    return (
                                      <FormGroup key={index}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={tempStaff?.role.includes(
                                                item.id
                                              )}
                                              onChange={() =>
                                                handleSelectRole(item.id)
                                              }
                                              inputProps={{
                                                "aria-label": "controlled",
                                              }}
                                            />
                                          }
                                          sx={{ marginLeft: 2 }}
                                          label={item.name}
                                          disabled={tempStaff?.title_id === 10}
                                        />
                                      </FormGroup>
                                    );
                                  })
                              : null}
                          </div>
                        </FormGroup>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={RETUR_ROLE.every((elem) =>
                                  tempStaff?.role.includes(elem)
                                )}
                                onChange={(_, checked) => {
                                  handleSelectCategory(checked, "retur");
                                }}
                                disabled={tempStaff?.title_id === 10}
                              />
                            }
                            label="Retur"
                          />
                          <div className="grid grid-cols-1 gap-x-4">
                            {dataRole && !isFetchingRole
                              ? dataRole
                                  .filter((item) =>
                                    RETUR_ROLE.includes(item.id)
                                  )
                                  .map((item, index) => {
                                    return (
                                      <FormGroup key={index}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={tempStaff?.role.includes(
                                                item.id
                                              )}
                                              onChange={() =>
                                                handleSelectRole(item.id)
                                              }
                                              inputProps={{
                                                "aria-label": "controlled",
                                              }}
                                              disabled={
                                                tempStaff?.title_id === 10
                                              }
                                            />
                                          }
                                          sx={{ marginLeft: 2 }}
                                          label={item.name}
                                        />
                                      </FormGroup>
                                    );
                                  })
                              : null}
                          </div>
                        </FormGroup>
                      </div>

                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={INVENTORY_ROLE.every((elem) =>
                                tempStaff?.role.includes(elem)
                              )}
                              onChange={(_, checked) => {
                                handleSelectCategory(checked, "inventory");
                              }}
                              disabled={tempStaff?.title_id === 10}
                            />
                          }
                          label="Inventory"
                        />
                        <div className="grid grid-cols-1 gap-x-4">
                          {dataRole && !isFetchingRole
                            ? dataRole
                                .filter((item) =>
                                  INVENTORY_ROLE.includes(item.id)
                                )
                                .map((item, index) => {
                                  return (
                                    <FormGroup key={index}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={tempStaff?.role.includes(
                                              item.id
                                            )}
                                            onChange={() =>
                                              handleSelectRole(item.id)
                                            }
                                            inputProps={{
                                              "aria-label": "controlled",
                                            }}
                                            disabled={
                                              tempStaff?.title_id === 10
                                            }
                                          />
                                        }
                                        sx={{ marginLeft: 2 }}
                                        label={item.name}
                                      />
                                    </FormGroup>
                                  );
                                })
                            : null}
                        </div>
                      </FormGroup>

                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={PATIENT_CARE_ROLE.every((elem) =>
                                tempStaff?.role.includes(elem)
                              )}
                              onChange={(_, checked) => {
                                handleSelectCategory(checked, "patientCare");
                              }}
                              disabled={tempStaff?.title_id === 10}
                            />
                          }
                          label="Patient Care"
                        />
                        <div className="grid grid-cols-1 gap-x-4">
                          {dataRole && !isFetchingRole
                            ? dataRole
                                .filter((item) =>
                                  PATIENT_CARE_ROLE.includes(item.id)
                                )
                                .map((item, index) => {
                                  return (
                                    <FormGroup key={index}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={tempStaff.role.includes(
                                              item.id
                                            )}
                                            onChange={() =>
                                              handleSelectRole(item.id)
                                            }
                                            inputProps={{
                                              "aria-label": "controlled",
                                            }}
                                            disabled={
                                              tempStaff?.title_id === 10
                                            }
                                          />
                                        }
                                        sx={{ marginLeft: 2 }}
                                        label={item.name}
                                      />
                                    </FormGroup>
                                  );
                                })
                            : null}
                        </div>
                      </FormGroup>
                    </div>
                  </div>

                  <hr className="my-4" />
                  <div>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={WAREHOUSE_ROLE.every((elem) =>
                              tempStaff?.role.includes(elem)
                            )}
                            onChange={(_, checked) => {
                              handleSelectCategory(checked, "warehouse");
                            }}
                          />
                        }
                        label="Central Warehouse"
                      />
                      <div className="grid grid-cols-4 gap-x-4">
                        {dataRole && !isFetchingRole
                          ? dataRole
                              .filter((item) =>
                                WAREHOUSE_ROLE.includes(item.id)
                              )
                              .map((item, index) => {
                                return (
                                  <FormGroup key={index}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={tempStaff.role.includes(
                                            item.id
                                          )}
                                          onChange={() =>
                                            handleSelectRole(item.id)
                                          }
                                          inputProps={{
                                            "aria-label": "controlled",
                                          }}
                                        />
                                      }
                                      sx={{ marginLeft: 2 }}
                                      label={item.name}
                                    />
                                  </FormGroup>
                                );
                              })
                          : null}
                      </div>
                    </FormGroup>
                  </div>

                  <hr className="my-4" />

                  <FormControl fullWidth sx={{ marginBottom: 2 }}>
                    <FormLabel>Nama Staff</FormLabel>
                    <TextField
                      value={tempStaff.fullname}
                      onChange={(e) =>
                        setTempStaff((prevVal) => ({
                          ...prevVal,
                          fullname: e.target.value,
                        }))
                      }
                    />
                  </FormControl>

                  {tempStaff.title_id === 3 ||
                  tempStaff.title_id === 4 ||
                  tempStaff.title_id === 7 ? (
                    <>
                      <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <FormLabel>Nomor Izin Apoteker/Tenaga Teknis</FormLabel>
                        <TextField
                          value={tempStaff.sipa_id}
                          onChange={(e) =>
                            setTempStaff((prevVal) => ({
                              ...prevVal,
                              sipa_id: e.target.value,
                            }))
                          }
                        />
                      </FormControl>

                      <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <FormLabel>
                          Masa Berlaku Nomor Izin Apoteker/Tenaga Teknis
                        </FormLabel>
                        <DesktopDatePicker
                          fullWidth
                          onChange={(date) =>
                            setTempStaff((prevVal) => ({
                              ...prevVal,
                              sipa_expired: moment(date).format("YYYY-MM-DD"),
                            }))
                          }
                          value={
                            tempStaff.sipa_expired === ""
                              ? null
                              : moment(tempStaff.sipa_expired)
                          }
                          renderInput={(params) => {
                            return <TextField {...params} fullWidth />;
                          }}
                        />
                      </FormControl>
                    </>
                  ) : null}
                  <FormControl fullWidth sx={{ marginBottom: 2 }}>
                    <FormLabel>No. Handphone</FormLabel>
                    <TextField
                      fullWidth
                      value={tempStaff.phone}
                      onChange={(e) =>
                        setTempStaff((prevVal) => ({
                          ...prevVal,
                          phone: e.target.value,
                        }))
                      }
                      InputProps={{
                        startAdornment: <InputAdornment>+62</InputAdornment>,
                      }}
                      error={
                        tempStaff.phone.length > 0 && tempStaff.phone[0] !== "8"
                      }
                      helperText={
                        tempStaff.phone.length > 0 && tempStaff.phone[0] !== "8"
                          ? "No HP tidak valid"
                          : null
                      }
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ marginBottom: 2 }}>
                    <FormLabel>Email</FormLabel>
                    <TextField
                      value={tempStaff.email}
                      onChange={(e) =>
                        setTempStaff((prevVal) => ({
                          ...prevVal,
                          email: e.target.value,
                        }))
                      }
                    />
                    <div>
                      {isPendingStatusEmail ? (
                        <CircularProgress size={10} />
                      ) : [4, 10, 11].includes(dataStatusEmail?.status_code) &&
                        tempStaff.email !== formik.values.owner.email ? (
                        <p className="text-[12px]">Email tersedia</p>
                      ) : (
                        <p className="text-red-500 text-[12px]">
                          {tempStaff.email === ""
                            ? "Email tidak boleh kosong"
                            : "Email sudah digunakan"}
                        </p>
                      )}
                    </div>
                  </FormControl>
                  {tempStaff.title_id === 12 ? (
                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                      <FormLabel>Nomor Surat Izin Praktek TTK</FormLabel>
                      <TextField
                        value={
                          tempStaff.evidence_pharmacist_technician
                            .practice_permit_number
                        }
                        onChange={(e) =>
                          setTempStaff((prevVal) => ({
                            ...prevVal,
                            evidence_pharmacist_technician: {
                              ...prevVal.evidence_pharmacist_technician,
                              practice_permit_number: e.target.value,
                            },
                          }))
                        }
                      />
                    </FormControl>
                  ) : null}

                  {tempStaff.title_id === 3 ||
                  tempStaff.title_id === 4 ||
                  tempStaff.title_id === 7 ||
                  tempStaff.title_id === 12 ? (
                    <div>
                      <p className="text-[16px] my-2 font-semibold">
                        Unggah File
                      </p>
                      {(tempStaff.title_id === 12
                        ? [
                            {
                              type: "suratTtk",
                              value:
                                tempStaff.evidence_pharmacist_technician
                                  .practice_permit,
                              file_type:
                                tempStaff.evidence_pharmacist_technician
                                  .practice_permit_type_file,
                              key: `staff[${index}].evidence_pharmacist_technician.practice_permit`,
                              title: "Surat Izin Praktek TTK",
                              input_tag: "ttk-upload",
                            },
                          ]
                        : [
                            {
                              type: "ktp",
                              value: tempStaff.files.ktp,
                              file_type: tempStaff.files.type_ktp,
                              key: `staff[${index}].files.ktp`,
                              title: "KTP",
                              input_tag: "ktp-upload",
                            },
                            {
                              type: "sipa",
                              value: tempStaff.files.izin,
                              file_type: tempStaff.files.type_izin,
                              key: `staff[${index}].files.izin`,
                              title: "Nomor Izin Apoteker/Tenaga Teknis",
                              input_tag: "sipa-upload",
                            },
                            {
                              type: "sign",
                              value: tempStaff.files.spesimen_signature,
                              file_type: tempStaff.files.type_signature,
                              key: `staff[${index}].files.spesimen_signature`,
                              title: "Spesimen Tanda Tangan",
                              input_tag: "sign-upload",
                            },
                          ]
                      ).map((item) => {
                        return (
                          <div className="mb-4">
                            {item.value === "" ? (
                              <>
                                <input
                                  type="file"
                                  id={item.input_tag}
                                  hidden
                                  accept="image/jpeg, image/png ,image/jpg, application/pdf"
                                  onChange={(e) => {
                                    formik.setFieldValue(item.key, "");
                                    if (item.type === "ktp")
                                      mutateUploadKTP(e.target.files[0]);
                                    if (item.type === "sipa")
                                      mutateUploadSIPA(e.target.files[0]);
                                    if (item.type === "sign")
                                      mutateUploadSign(e.target.files[0]);
                                    if (item.type === "suratTtk")
                                      mutateUploadTtk(e.target.files[0]);
                                  }}
                                />
                                <label for={item.input_tag} className="w-full">
                                  <div className="w-full bg-blue-500 text-white py-3 px-4 rounded-t-lg flex items-center gap-2">
                                    <FaRegFileAlt className="text-[16px]" />
                                    <p>{item.title}</p>
                                  </div>
                                  <div className="w-full border-dashed p-4 border-b-2 border-l-2 border-r-2 border-gray-300 rounded-b-lg cursor-pointer hover:text-gray-300 flex flex-col justify-center items-center">
                                    {(item.type === "ktp" &&
                                      isPendingUploadKTP) ||
                                    (item.type === "sipa" &&
                                      isPendingUploadSIPA) ||
                                    (item.type === "sign" &&
                                      isPendingUploadSign) ||
                                    (item.type === "suratTtk" &&
                                      isPendingUploadTtk) ? (
                                      <CircularProgress />
                                    ) : (
                                      <IoIosCloudUpload className="text-[64px] text-blue-500" />
                                    )}
                                    <p>Unggah {item.title}</p>
                                  </div>
                                </label>
                              </>
                            ) : (
                              <div>
                                <SuccessfulUploadComponent
                                  title={item.title}
                                  value={item.value}
                                  type={item.type}
                                  fileType={item.file_type}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  <div className="flex gap-2 items-center">
                    {index < 0 ? null : (
                      <button
                        type="button"
                        className="text-[12px] w-full p-2 !rounded-md bg-red-500 hover:bg-gray-300 text-white disabled:bg-gray-300"
                        onClick={handleDeleteClose}
                      >
                        Hapus
                      </button>
                    )}
                    <button
                      type="button"
                      className="text-[12px] w-full p-2 !rounded-md bg-blue-500 hover:bg-gray-300 text-white disabled:bg-gray-300"
                      onClick={handleCloseSave}
                      disabled={
                        tempStaff.title_id === 0 ||
                        tempStaff.role.length === 0 ||
                        tempStaff.fullname === "" ||
                        tempStaff.phone === "" ||
                        tempStaff.phone.length > 15 ||
                        (tempStaff.length > 0 && tempStaff[0] !== "8") ||
                        tempStaff.email.length === 0 ||
                        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tempStaff.email) ||
                        ((tempStaff.title_id === 3 ||
                          tempStaff.title_id === 4 ||
                          tempStaff.title_id === 7) &&
                          (tempStaff.sipa_id === "" ||
                            tempStaff.sipa_expired === "" ||
                            tempStaff.files.ktp === "" ||
                            tempStaff.files.izin === "" ||
                            tempStaff.files.spesimen_signature === "")) ||
                        (tempStaff.title_id === 12 &&
                          (tempStaff.evidence_pharmacist_technician
                            .practice_permit === "" ||
                            tempStaff.evidence_pharmacist_technician
                              .practice_permit_number === "")) ||
                        !(
                          [4, 10, 11].includes(dataStatusEmail?.status_code) &&
                          tempStaff.email !== formik.values.owner.email
                        ) ||
                        isPendingStatusEmail
                      }
                    >
                      Simpan
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <CircularProgress size={20} />
            )}
          </div>
        </Box>
      </Modal>

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );
};

export default ModalAddStaff;
